// IGK-2P v2.1 Phase-3 — Keyframe-stage compact status bar (DELTA-2).
// One-line pipeline status on grid cards (full timeline lives only in the CD
// bench). Click opens the guided review wizard. Mounts INSIDE KeyframeCard.
// LEGACY shots (no IGK-2P run) get a plain "Legacy" row with a Generate
// (IGK-2P) affordance + a Review link that opens the bench in its guarded state
// (CD ruling 2026-07-15: legacy = (ii) guarded bench + (i) card-level Generate).
import React, { useState } from 'react';
import { Lock, Loader2, Sparkles } from 'lucide-react';
import { toast } from '../../utils/toast';
import { blueprintState, resolveLook, hasIgk2pRun, regenIGK2P } from './igk2pApi';
import Igk2pCdBench from './Igk2pCdBench';

function Pip({ label, tone, title }) {
  const cls = {
    ok: 'bg-emerald-500/20 text-emerald-300',
    warn: 'bg-amber-500/20 text-amber-300',
    bad: 'bg-red-500/25 text-red-300',
    off: 'bg-white/5 text-white/35',
  }[tone];
  return <span title={title} className={`px-1 py-[1px] rounded text-[8px] font-medium ${cls}`}>{label}</span>;
}

export default function Igk2pStatusBar({ shot, onReload }) {
  const [openBench, setOpenBench] = useState(false);
  const [busy, setBusy] = useState(false);
  const isRun = hasIgk2pRun(shot);

  const doGenerate = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const r = await regenIGK2P(shot.id, { allow_missing_sketch: true, allow_stale_bindings: true, allow_empty_canon: true });
      if (r.success) toast.success('IGK-2P generation queued — appears here when ready');
      else toast.error(`Blocked: ${r.reason || r.message || 'gate blocked'}`);
      onReload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generate failed');
    } finally { setBusy(false); }
  };

  // ── LEGACY / no IGK-2P run → plain row, not the 2P chip bar ──
  if (!isRun) {
    return (
      <>
        <div className="w-full mt-1 flex items-center gap-1.5 px-1.5 py-1 rounded-md bg-black/30 border border-dashed border-white/10" data-testid={`igk2p-legacy-row-${shot.id}`}>
          <span className="text-[8px] font-medium text-white/40 shrink-0">Legacy frame</span>
          <button
            data-testid={`igk2p-legacy-generate-${shot.id}`}
            disabled={busy}
            onClick={doGenerate}
            title="Generate this shot through IGK-2P (enables blueprint fidelity + grading review)"
            className="ml-auto inline-flex items-center gap-1 px-1.5 py-[2px] rounded text-[8px] font-semibold bg-fuchsia-500/20 text-fuchsia-200 hover:bg-fuchsia-500/35 disabled:opacity-40 transition-colors"
          >
            {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Sparkles className="w-2.5 h-2.5" />} Generate (IGK-2P)
          </button>
          <button
            data-testid={`igk2p-legacy-review-${shot.id}`}
            onClick={(e) => { e.stopPropagation(); setOpenBench(true); }}
            className="inline-flex items-center px-1.5 py-[2px] rounded text-[8px] font-medium bg-white/5 text-white/60 hover:bg-white/10 transition-colors"
          >Review</button>
        </div>
        {openBench && <Igk2pCdBench shot={shot} onClose={() => setOpenBench(false)} onReload={onReload} />}
      </>
    );
  }

  const bp = blueprintState(shot);
  const kfStatus = shot?.keyframe_hyperreal_status || shot?.keyframe_igk2p_status;
  const hasKf = !!(shot?.keyframe_hyperreal_url || shot?.keyframe_hyperreal_s3_url);
  const hasLog = !!(shot?.keyframe_hyperreal_slog3_url || shot?.keyframe_hyperreal_slog3_s3_url);
  const locked = !!shot?.igk2p_lock;
  const look = resolveLook(shot?.scene_number);
  const needsReview = kfStatus === 'needs_manual_review';
  // Stuck-GENERATING → loud stalled state after 8 min with no result (item 6).
  const genSince = shot?.keyframe_hyperreal_generating_since;
  const stalled = kfStatus === 'generating' && genSince &&
    (Date.now() - new Date(genSince).getTime()) > 8 * 60 * 1000;

  const bpTone = { green: 'ok', amber: 'warn', red: 'bad', grey: 'off' }[bp];
  const composeTone = stalled ? 'bad' : kfStatus === 'generating' ? 'warn' : needsReview ? 'bad' : hasKf ? 'ok' : 'off';
  const labTone = hasLog ? 'ok' : 'off';
  const lockTone = locked ? 'ok' : 'off';

  return (
    <>
      <button
        data-testid={`igk2p-status-bar-${shot.id}`}
        onClick={(e) => { e.stopPropagation(); setOpenBench(true); }}
        title={needsReview ? 'Needs manual review — open the bench' : 'Open review bench (framing · colour · lock)'}
        className="w-full mt-1 flex items-center gap-1 px-1.5 py-1 rounded-md bg-black/40 border border-white/10 hover:border-fuchsia-400/40 transition-colors overflow-hidden"
      >
        <span className="text-[8px] font-bold tracking-wider text-fuchsia-300/80 shrink-0">2P</span>
        <Pip label="BP" tone={bpTone} title={`Blueprint: ${bp}`} />
        <Pip label="Cmp" tone={composeTone} title={needsReview ? 'Needs manual review' : 'Compose (Pass-1)'} />
        <Pip label="Exp" tone={hasKf ? 'ok' : 'off'} title="Exposure gate" />
        <Pip label="J1" tone={hasKf ? 'ok' : 'off'} title="Judge-1" />
        <Pip label="Lab" tone={labTone} title="S-Log3 conform" />
        <Pip label={look === 'AU_Dusk' ? 'Dusk' : 'Day'} tone={hasLog ? 'ok' : 'off'} title={`Look ${look}`} />
        <Pip label="J2" tone={hasLog ? 'ok' : 'off'} title="Judge-2" />
        {needsReview && <Pip label="Review" tone="bad" title="Needs manual review" />}
        {stalled && <Pip label="Stalled" tone="bad" title="Generating >8 min with no result — click to retry" />}
        <span className="ml-auto shrink-0">
          {locked ? <Lock className="w-2.5 h-2.5 text-emerald-400" /> : <Pip label="Lock" tone={lockTone} title="Not locked" />}
        </span>
      </button>
      {openBench && <Igk2pCdBench shot={shot} onClose={() => setOpenBench(false)} onReload={onReload} />}
    </>
  );
}
